/**
 * Installation de WaitechEdu comme une application (PWA) : sur Android, Chrome et Edge, le navigateur propose
 * l'installation (événement `beforeinstallprompt`, gardé ici dès le chargement) ; sur iPhone et iPad, elle se
 * fait à la main par « Partager › Sur l'écran d'accueil ».
 */
import { useSyncExternalStore } from "react";

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault(); // Pas de bandeau automatique : l'installation se propose depuis le menu du compte.
    deferred = event as InstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    notify();
  });
}

/** Déjà ouverte comme application installée. */
export const isInstalled = (): boolean =>
  window.matchMedia?.("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;

/** iPhone ou iPad (installation manuelle depuis Safari). */
export const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export type InstallMode = "prompt" | "ios" | null;

function mode(): InstallMode {
  if (isInstalled()) return null;
  if (deferred) return "prompt";
  return isIos() ? "ios" : null;
}

/** Comment proposer l'installation sur cet appareil (null : déjà installée ou navigateur sans installation). */
export function useInstallMode(): InstallMode {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    mode,
    () => null,
  );
}

/** Ouvre la fenêtre d'installation du navigateur ; vrai si l'utilisateur a accepté. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const event = deferred;
  deferred = null;
  await event.prompt();
  const { outcome } = await event.userChoice;
  notify();
  return outcome === "accepted";
}
